import { useEffect, useState } from 'react';
import { Button, Checkbox, Dialog, Divider, FormLabel, useModalPortal, Wrapper } from '@screentone/core';
import { cloneDeep } from 'lodash';

import { BehindFeatureFlag } from 'components/behind-feature-flag/BehindFeatureFlag';
import { ItemCountOverrideFields } from 'components/item-count-override-fields/ItemCountOverrideFields';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { usePageTypeSettingsContext } from 'contexts/page-type-settings/usePageTypeSettings';
import { AvailableFeatureFlags, PageModule, ParametersType, UiModuleType, ValidationKey } from 'data/generated/graphql';
import { useIsAdmin, useIsSuper } from 'hooks/roles';
import { getModule } from 'utils/modules';
import AllessehModuleId from './components/allesseh-module-id/AllessehModuleId';
import Entitlement from './components/entitlementsManager/EntitlementsManager';
import { ModuleParameters } from './components/ModuleParameters/ModuleParameters';
import { PageModuleBasic } from './components/page-module-basic/PageModuleBasic';
import { PageModuleRanked } from './components/page-module-ranked/PageModuleRanked';
import { PageModuleText } from './components/page-module-text/PageModuleText';
import { PageModuleTitle } from './components/page-module-title/PageModuleTitle';
import { PageModuleTwoColumn } from './components/page-module-two-column/PageModuleTwoColumn';
import Ribbon from './components/ribbon/Ribbon';
import styles from './PageModuleModal.module.scss';

interface PageModuleModalProps {
  onChange: (
    newPageModule: PageModule,
    moduleId: string,
    hasCustomModuleId: boolean,
    entitlements: string[],
    ribbonUrl?: string
  ) => void;
  onDismiss: () => void;
  pageModule: PageModule;
  showIdEdit: boolean;
  showEntitlements: boolean;
  moduleId?: string;
  hasCustomModuleId?: boolean;
  ribbonUrl?: string;
  entitlements?: string[];
  viewOnly?: boolean;
  disableModuleIdEdit?: boolean;
}

interface PageModuleModalFormProps {
  pageModule: PageModule;
  onChange: (newPageModule: PageModule) => void;
  viewOnly?: boolean;
  initialPageModule: PageModule;
}

const PageModuleModalForm = ({ pageModule, onChange, viewOnly, initialPageModule }: PageModuleModalFormProps) => {
  const { uiModuleType } = pageModule;
  let childModule = null;

  switch (uiModuleType) {
    case UiModuleType.UiRankedModuleType:
      childModule = <PageModuleRanked pageModule={pageModule} onChange={onChange} viewOnly={viewOnly} />;
      break;
    case UiModuleType.UiBasicModuleType:
    case UiModuleType.UiTitleModuleType:
    case UiModuleType.UiTitlePinnedModuleType:
      childModule = (
        <PageModuleTitle
          pageModule={pageModule}
          onChange={onChange}
          viewOnly={viewOnly}
          initialPageModule={initialPageModule}
        />
      );
      break;
    case UiModuleType.UiTextModuleType:
      childModule = <PageModuleText pageModule={pageModule} onChange={onChange} viewOnly={viewOnly} />;
      break;
    case UiModuleType.UiBasicOptionalModuleType:
      childModule = <PageModuleBasic />;
      break;
    case UiModuleType.UiFeedsModuleType:
      childModule = <PageModuleBasic />;
      break;
    default:
      childModule = <PageModuleTwoColumn />;
  }

  return childModule;
};

export const PageModuleModal = ({
  pageModule,
  onChange,
  onDismiss,
  viewOnly,
  showIdEdit,
  showEntitlements,
  moduleId = '',
  hasCustomModuleId = false,
  entitlements = [],
  disableModuleIdEdit = false,
  ...props
}: PageModuleModalProps) => {
  const initialRibbonUrl = props.ribbonUrl ?? '';
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  const { renderNode } = useModalPortal();
  const [error, setError] = useState(false);
  const [ribbonError, setRibbonError] = useState(false);
  const [activePageModule, setActivePageModule] = useState(pageModule);
  const [ribbonUrl, setRibbonUrl] = useState(initialRibbonUrl);

  // Separate flag for UI display: show moduleId if it's custom OR if it's read-only
  const shouldDisplayModuleId = hasCustomModuleId || disableModuleIdEdit;
  const [activeModuleId, setActiveModuleId] = useState(shouldDisplayModuleId ? moduleId : '');
  const [activeEntitlements, setActiveEntitlements] = useState(entitlements);

  const isAdmin = useIsAdmin();
  const isSuper = useIsSuper();
  // Visible to admins/supers regardless of the page lock, but still disabled while viewOnly —
  // editing these requires acquiring publishing rights to avoid clashing with the current lock holder.
  const canEditAdvanced = isAdmin || isSuper;

  const { getTreatmentTypeSetting } = usePageTypeSettingsContext();
  const treatmentTypeSetting = getTreatmentTypeSetting(activePageModule);
  const isFeedsModule = activePageModule.uiModuleType === UiModuleType.UiFeedsModuleType;

  const { validations } = useDataModelContext();
  const minValidation = validations.find((v) => v.name === ValidationKey.Min);
  const maxValidation = validations.find((v) => v.name === ValidationKey.Max);
  const showMinOverride = typeof minValidation?.enabled === 'boolean' ? minValidation.enabled : true;
  const showMaxOverride = typeof maxValidation?.enabled === 'boolean' ? maxValidation.enabled : false;

  const [minItemsOverride, setMinItemsOverride] = useState(
    pageModule.minItemsOverride != null ? String(pageModule.minItemsOverride) : ''
  );
  const [maxItemsOverride, setMaxItemsOverride] = useState(
    pageModule.maxItemsOverride != null ? String(pageModule.maxItemsOverride) : ''
  );

  // Initialize showBullets to false if undefined (so it gets serialized to Allesseh)
  useEffect(() => {
    const initializedPageModule = cloneDeep(pageModule);
    const module = getModule(initializedPageModule);
    if (module && module.showBullets === undefined) {
      module.showBullets = false;
    }
    setActivePageModule(initializedPageModule);
  }, [pageModule]);

  const handleshowBulletsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPageModule = cloneDeep(activePageModule);
    const module = getModule(newPageModule);
    if (module) {
      module.showBullets = e.target.checked;
      setActivePageModule(newPageModule);
    }
  };

  const handlePageModuleChange = (newPageModule: PageModule) => {
    // Preserve the showBullets value when other fields change
    const currentModule = getModule(activePageModule);
    const newModule = getModule(newPageModule);
    if (currentModule && newModule) {
      newModule.showBullets = currentModule.showBullets;
    }
    setActivePageModule((prev) => ({ ...newPageModule, moduleParameters: prev.moduleParameters }));
  };

  const handleModuleParametersChange = (updated: ParametersType[]) => {
    setActivePageModule((prev) => ({ ...prev, moduleParameters: updated }));
  };

  const parsedMin =
    minItemsOverride !== '' && Number.isInteger(Number(minItemsOverride)) && Number(minItemsOverride) >= 0
      ? Number(minItemsOverride)
      : undefined;
  const parsedMax =
    maxItemsOverride !== '' && Number.isInteger(Number(maxItemsOverride)) && Number(maxItemsOverride) >= 0
      ? Number(maxItemsOverride)
      : undefined;
  const effectiveMin = parsedMin ?? treatmentTypeSetting?.minRequiredContentItems ?? undefined;
  const effectiveMax = parsedMax ?? treatmentTypeSetting?.maxRequiredContentItems ?? undefined;
  const overrideLimitsError =
    effectiveMin !== undefined && effectiveMax !== undefined && effectiveMin > effectiveMax
      ? 'Min must not be greater than max.'
      : undefined;

  const handleSave = () => {
    if (overrideLimitsError) return;
    const trimmedModuleId = activeModuleId.trim();

    // Determine if the module ID should be marked as custom:
    // - If editing is disabled, preserve the original hasCustomModuleId flag (read-only IDs aren't user overrides)
    // - Otherwise, mark as custom if user provided a non-empty ID
    const isCustom = disableModuleIdEdit ? hasCustomModuleId : trimmedModuleId.length > 0;

    const moduleWithOverrides: PageModule = {
      ...activePageModule,
      minItemsOverride: parsedMin,
      maxItemsOverride: parsedMax
    };

    onChange(moduleWithOverrides, trimmedModuleId, isCustom, activeEntitlements, ribbonUrl);
    onDismiss();
  };

  return (
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    <Dialog status="open" onDismiss={onDismiss} focusTrap={false} renderNode={renderNode}>
      <Dialog.Title>Module Options</Dialog.Title>
      <Dialog.Content>
        <div className={styles.scrollableContent}>
          <PageModuleModalForm
            pageModule={activePageModule}
            viewOnly={viewOnly}
            onChange={handlePageModuleChange}
            initialPageModule={pageModule}
          />
          {/* // TODO: Remove ribbon feature flag check after Ribbon is fully launched */}
          <BehindFeatureFlag featureFlagName={AvailableFeatureFlags.ShowRibbonMvp}>
            <Ribbon url={ribbonUrl} error={ribbonError} setError={setRibbonError} onChange={setRibbonUrl} disabled={viewOnly} />
          </BehindFeatureFlag>
          {showIdEdit && (
            <AllessehModuleId
              moduleId={activeModuleId}
              error={error}
              setError={setError}
              onChange={setActiveModuleId}
              disabled={disableModuleIdEdit || viewOnly}
            />
          )}
          {showEntitlements && (
            <Entitlement value={activeEntitlements} onChange={setActiveEntitlements} isReadOnly={viewOnly} />
          )}
          {treatmentTypeSetting && canEditAdvanced && !isFeedsModule && (
            <ItemCountOverrideFields
              treatmentTypeSetting={treatmentTypeSetting}
              minItemsOverride={minItemsOverride}
              maxItemsOverride={maxItemsOverride}
              onMinChange={setMinItemsOverride}
              onMaxChange={setMaxItemsOverride}
              validationError={overrideLimitsError}
              showMinOverride={showMinOverride}
              showMaxOverride={showMaxOverride}
              disabled={viewOnly}
            />
          )}
          <BehindFeatureFlag featureFlagName={AvailableFeatureFlags.ShowBulletsEnabled}>
            {canEditAdvanced && (
              <FormLabel label="Show Bullets" labelPosition="right" fullWidth margin={{ top: 'sm' }}>
                <Checkbox
                  checked={getModule(activePageModule)?.showBullets ?? false}
                  onChange={handleshowBulletsChange}
                  disabled={viewOnly}
                  data-testid="page-display-bullets"
                />
              </FormLabel>
            )}
          </BehindFeatureFlag>
          {canEditAdvanced && (
            <ModuleParameters
              moduleParameters={activePageModule.moduleParameters ?? []}
              onChange={handleModuleParametersChange}
              disabled={viewOnly}
            />
          )}
        </div>
        <Divider className={styles.divider} />
        <Wrapper className={styles.buttons}>
          {!viewOnly && (
            <>
              <Button secondary onClick={onDismiss} margin={{ right: 'sm' }} data-testid="page-treatment-type-cancel">
                Cancel
              </Button>
              <Button
                disabled={error || ribbonError}
                primary
                onClick={handleSave}
                data-testid="page-treatment-type-save"
              >
                Save
              </Button>
            </>
          )}
        </Wrapper>
      </Dialog.Content>
    </Dialog>
  );
};
